import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import en, { Dictionary } from './en';
import km from './km';
import {
  Locale,
  localeFromPathname,
  localePath,
} from './locales';

export {
  LOCALES,
  type Locale,
  DEFAULT_LOCALE,
  LOCALE_LABELS,
  OG_LOCALE,
  LANGUAGE_STORAGE_KEY,
  isLocale,
  localeFromPathname,
  localePath,
  preferredLocale,
} from './locales';

interface I18nValue {
  locale: Locale;
  /** Dictionary for the current locale (typed, so all keys exist). */
  t: Dictionary;
  /** Localized path helper bound to the current locale. */
  localePath: (path: string) => string;
  /** Build the URL of the current page in another locale. */
  switchTo: (next: Locale) => { pathname: string; search: string; hash: string } | null;
}

const dictionaries: Record<Locale, Dictionary> = { en, km };

const I18nContext = createContext<I18nValue | null>(null);

export const I18nProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { pathname, search, hash } = useLocation();
  const locale = localeFromPathname(pathname);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const value = useMemo<I18nValue>(() => {
    const trimmed = pathname.slice(1 + locale.length) || '/';
    return {
      locale,
      t: dictionaries[locale],
      localePath: (path: string) => localePath(locale, path),
      switchTo: (next: Locale) => {
        if (next === locale) return null;
        return {
          pathname: localePath(next, trimmed),
          search,
          hash,
        };
      },
    };
  }, [locale, pathname, search, hash]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return ctx;
}

export type { Dictionary };
